// WhatsApp reminders card: the DOM builders (no network, no cache, no global state: everything arrives in ctx). C2 covers Not connected, the guide, Requested and Declined; the In setup checklist (C3) and the
// Connected card (C4, C5) are placeholders here (title and chip only) until their sub-commits.
// ctx = { doc, t, lang, resolved, act, integ, ui: {screen}, load: {error, unavailable}|null, on: {activate, back, ask, cancel, retry}, busy }
import { STATES, TIMINGS, OPTION_KEYS, EXAMPLE_KEY, checklist, readiness, connectionChip, liveNotReady, consentSummary, lastSendDate, hourVisible, hourOptions, hourLabel, manualAllowed, modeRowVisible } from './state.js';

const FSI = String.fromCharCode(0x2068), PDI = String.fromCharCode(0x2069);
export const iso = s => FSI + String(s) + PDI;                    // a bidi ISOLATE around a value that may be Latin text or digits inside a Hebrew sentence (and the reverse)

export function h(doc, tag, props, kids) {
  const e = doc.createElement(tag);
  const p = props || {};
  for (const k of Object.keys(p)) {
    const v = p[k];
    if (v === undefined || v === null || v === false) continue;
    if (k === 'class') e.className = v;
    else if (k === 'text') e.textContent = v;
    else if (k === 'html') e.innerHTML = v;                         // only for the static SVG strings below
    else if (k.startsWith('on') && typeof v === 'function') e[k] = v;
    else e.setAttribute(k, v === true ? '' : String(v));
  }
  (kids || []).forEach(c => { if (c === null || c === undefined || c === false) return; e.appendChild(typeof c === 'string' ? doc.createTextNode(c) : c); });
  return e;
}

const SVG = (inner) => '<svg viewBox="0 0 52 52" fill="none" stroke="#0d6b63" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + inner + '</svg>';
export const ICON = {
  phone: SVG('<rect x="15" y="6" width="22" height="40" rx="5"/><path d="M23 40h6"/>'),
  link: SVG('<path d="M21 31l10-10"/><path d="M25 17l3-3a8 8 0 0111 11l-3 3"/><path d="M27 35l-3 3a8 8 0 01-11-11l3-3"/>'),
  msg: SVG('<path d="M10 12h32v21H24l-9 8v-8h-5z"/><path d="M17 20h18M17 26h11"/>'),
  test: SVG('<circle cx="26" cy="26" r="16"/><path d="M18 27l6 6 11-13"/>'),
  go: SVG('<path d="M26 8v26"/><path d="M16 18l10-10 10 10"/><path d="M12 42h28"/>')
};
const STEPS = [['phone', 1], ['link', 2], ['msg', 3], ['test', 4], ['go', 5]];

export function fmtDate(isoStr, lang) {
  const d = new Date(isoStr); if (isNaN(d.getTime())) return '';
  try { return new Intl.DateTimeFormat(lang === 'he' ? 'he-IL' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Jerusalem' }).format(d); }
  catch (_) { return d.toISOString().slice(0, 10); }
}
const PREF_BY = { phone: 'wac_pref_by_phone', whatsapp: 'wac_pref_by_whatsapp', email: 'wac_pref_by_email' };

function btn(ctx, label, cls, onclick, attrs) { return h(ctx.doc, 'button', Object.assign({ type: 'button', class: 'ck-btn ' + (cls || ''), text: label, onclick }, attrs || {})); }

function empty(ctx) {
  const { doc, t } = ctx;
  return h(doc, 'section', { class: 'wac-card wac-hero', 'aria-labelledby': 'wac-h' }, [
    h(doc, 'span', { html: ICON.msg.replace('<svg', '<svg style="width:120px;height:90px"') }),
    h(doc, 'h2', { id: 'wac-h', text: t('wac_empty_title') }),
    h(doc, 'p', { class: 'wac-sub', text: t('wac_empty_sub') }),
    h(doc, 'div', { class: 'wac-actions' }, [btn(ctx, t('wac_activate'), 'primary', ctx.on.activate, { 'data-wac': 'activate' })])
  ]);
}

function guide(ctx) {
  const { doc, t } = ctx;
  const items = STEPS.map(([icon, n]) => h(doc, 'li', {}, [
    h(doc, 'span', { html: ICON[icon] }),
    h(doc, 'div', { style: 'min-width:0;flex:1' }, [
      h(doc, 'div', { class: 'wac-sn', text: t('wac_step_n', { n }) }),
      h(doc, 'div', { class: 'wac-st', text: t('wac_step' + n + '_t') }),
      h(doc, 'div', { class: 'wac-sd', text: t('wac_step' + n + '_d') })
    ])
  ]));
  return h(doc, 'section', { class: 'wac-card', 'aria-labelledby': 'wac-h' }, [
    h(doc, 'h2', { id: 'wac-h', text: t('wac_guide_title') }),
    h(doc, 'p', { class: 'wac-sub', text: t('wac_guide_sub') }),
    h(doc, 'ol', { class: 'wac-steps', 'aria-label': t('wac_aria_steps') }, items),
    h(doc, 'div', { class: 'wac-info', text: t('wac_good_to_know') }),
    // TP-SUNSET (HF-20261101-0003): wac_guide_ownership and wac_guide_cost describe the INTERIM era (hosted by us, bundled cost). They become FALSE on Tech Provider approval: revise EN + HE then (Hebrew via Eytan). Standing line on the WA-GOLIVE-REAL card.
    h(doc, 'div', { class: 'wac-info', text: t('wac_guide_cost') }),
    h(doc, 'div', { class: 'wac-info', text: t('wac_guide_ownership') }),
    h(doc, 'div', { class: 'wac-actions' }, [
      btn(ctx, t('wac_ask'), 'primary', ctx.on.ask, { 'data-wac': 'ask' }),
      btn(ctx, t('btn_back'), 'ghost', ctx.on.back, { 'data-wac': 'back' })
    ])
  ]);
}

function requested(ctx) {
  const { doc, t, act, lang } = ctx; const r = act.request;
  const pref = t(PREF_BY[r.contact_pref] || 'wac_pref_by_phone');
  const when = r.best_time ? String(r.best_time) : '';
  const sub = when ? t('wac_requested_sub_when', { d: iso(fmtDate(r.requested_at, lang)), pref, when: iso(when) }) : t('wac_requested_sub', { d: iso(fmtDate(r.requested_at, lang)), pref });
  return h(doc, 'section', { class: 'wac-card', 'aria-labelledby': 'wac-h' }, [
    h(doc, 'h2', { id: 'wac-h', text: t('wac_requested_title') }),
    h(doc, 'p', { class: 'wac-sub', text: sub }),
    h(doc, 'ul', { class: 'wac-check' }, [h(doc, 'li', { class: 'now' }, [h(doc, 'span', { class: 'wac-b', text: '•', 'aria-hidden': 'true' }), h(doc, 'span', { text: t('wac_requested_wait') })])]),
    h(doc, 'div', { class: 'wac-actions' }, [btn(ctx, t('wac_cancel_req'), 'ghost', ctx.on.cancel, { 'data-wac': 'cancel' })])
  ]);
}

// In setup (C3): the checklist the head can read. Ticks come from the counts the server computed; "test message received" and "consent ready" wait for the operator's step (state.js checklist()).
function inSetup(ctx) {
  const { doc, t, act, integ, lang } = ctx;
  const cl = checklist(act);
  const when = integ && integ.templates_checked_at ? fmtDate(integ.templates_checked_at, lang) : '';
  const LABEL = { request: 'wac_ck_request', number: 'wac_ck_number', messages: 'wac_ck_msgs', approved: 'wac_ck_approved', test: 'wac_ck_test', consent: 'wac_ck_consent' };
  const detail = (it) => {
    if (it.key === 'number' && it.done && it.last4) return t('wac_ck_number_d', { n: iso(it.last4) });
    if (it.key === 'messages' && it.m > 0 && (it.done || it.n > 0)) return t('wac_ck_msgs_d', { n: it.n, m: it.m });
    if (it.key === 'approved') return it.done ? (when ? t('wac_ck_approved_done', { d: iso(when) }) : '') : (it.current ? t('wac_ck_approved_d') : '');
    if (it.key === 'consent') return t('wac_ck_consent_d');
    return '';
  };
  const items = cl.items.map(it => {
    const d = detail(it);
    return h(doc, 'li', { class: it.done ? 'done' : (it.current ? 'now' : ''), 'data-wac-ck': it.key, 'data-done': it.done ? '1' : '0', 'aria-current': it.current ? 'step' : null }, [
      h(doc, 'span', { class: 'wac-b', text: it.done ? '✓' : (it.current ? '•' : ''), 'aria-hidden': 'true' }),
      h(doc, 'span', { style: 'min-width:0;flex:1' }, [h(doc, 'span', { text: t(LABEL[it.key]) }), d ? h(doc, 'span', { class: 'wac-w', text: d }) : null])
    ]);
  });
  return h(doc, 'section', { class: 'wac-card', 'aria-labelledby': 'wac-h' }, [
    h(doc, 'h2', { id: 'wac-h', text: t('wac_setup_title') }),
    h(doc, 'p', { class: 'wac-sub', text: t('wac_setup_sub') }),
    h(doc, 'ul', { class: 'wac-check', 'aria-label': t('wac_aria_progress') }, items),
    h(doc, 'div', { class: 'wac-info', text: t('wac_setup_note') })
  ]);
}

// ---- Connected (C4: the READ-ONLY blocks; C5 adds When to remind, the Shabbat toggle and the switch/disconnect in their mock positions) ----
const sec = (ctx, title, kids) => h(ctx.doc, 'div', { class: 'wac-sec' }, [h(ctx.doc, 'h3', { class: 'wac-sh', text: title })].concat(kids));
const kv = (ctx, k, valueNode) => [h(ctx.doc, 'div', { class: 'wac-k', text: k }), h(ctx.doc, 'div', { class: 'wac-v' }, [valueNode])];
const txtNode = (ctx, s) => ctx.doc.createTextNode(s);
const chipNode = (ctx, cls, label) => h(ctx.doc, 'span', { class: 'wac-chip ' + cls, text: label });

function connectionBlock(ctx) {
  const { doc, t, integ, lang, resolved } = ctx; const chip = connectionChip(integ, resolved.paused);
  const rows = [];
  rows.push(...kv(ctx, t('wac_k_status'), chipNode(ctx, chip === 'paused' ? 'off' : chip, t(chip === 'paused' ? 'wac_chip_paused' : (chip === 'live' ? 'wac_chip_live' : 'wac_chip_test')))));
  if (integ.display_phone_last4) rows.push(...kv(ctx, t('wac_k_from'), txtNode(ctx, t('wac_from_val', { n: iso(integ.display_phone_last4) }))));
  if (integ.token_present === true) rows.push(...kv(ctx, t('wac_k_conn'), txtNode(ctx, t('wac_conn_val'))));
  const last = lastSendDate(integ);
  rows.push(...kv(ctx, t('wac_k_last'), txtNode(ctx, last ? iso(fmtDate(last + 'T12:00:00Z', lang)) : t('wac_last_none'))));
  const kids = [h(doc, 'div', { class: 'wac-kv' }, rows)];
  if (chip === 'test' && Number.isInteger(integ.test_allow_count) && integ.test_allow_count >= 0) kids.push(h(doc, 'div', { class: 'wac-warn', text: integ.test_allow_count === 1 ? t('wac_test_warn_one') : t('wac_test_warn_other', { n: integ.test_allow_count }) }));
  if (chip === 'paused') kids.push(h(doc, 'div', { class: 'wac-info', text: t('wac_paused_info') }));
  return sec(ctx, t('wac_sec_connection'), kids);
}
function quietBlock(ctx) {
  const { doc, t } = ctx;
  return sec(ctx, t('wac_sec_quiet'), [h(doc, 'div', { class: 'wac-kv' }, kv(ctx, t('wac_quiet_k'), txtNode(ctx, t('wac_quiet_v'))))].concat(shabbatRow(ctx)));
}
function messageBlock(ctx) {
  const { doc, t, integ, lang } = ctx; const rd = readiness(integ); const last = lastSendDate(integ); const live = integ.status === 'live';
  const rows = [];
  rows.push(...kv(ctx, t('wac_name_k'), txtNode(ctx, t('wac_name_v'))));
  const chipKey = { ready: ['live', 'wac_tpl_ready'], problem: ['bad', 'wac_tpl_problem'], unconfirmed: ['test', 'wac_tpl_unconf'] }[rd.kind];
  const detail = rd.kind === 'ready' ? t('wac_tpl_ready_d', { n: rd.total, d: iso(rd.checkedAt ? fmtDate(rd.checkedAt, lang) : '') }) : (rd.kind === 'problem' ? t('wac_tpl_problem_d') : t('wac_tpl_unconf_d'));
  rows.push(...kv(ctx, t('wac_msg_k'), h(doc, 'span', {}, [chipNode(ctx, chipKey[0], t(chipKey[1])), ' ', h(doc, 'span', { text: detail })])));
  const kids = [h(doc, 'div', { class: 'wac-kv' }, rows), h(doc, 'p', { class: 'wac-hint', text: t('wac_tpl_hint') })];
  if (liveNotReady(integ)) kids.push(h(doc, 'div', { class: 'wac-warn', text: t('wac_tpl_live_warn') }));
  if (last) kids.push(h(doc, 'div', { class: 'wac-kv' }, kv(ctx, live && rd.kind !== 'ready' ? t('wac_att_k_hist') : t('wac_att_k'), txtNode(ctx, t(live && rd.kind !== 'ready' ? 'wac_att_test_sent' : 'wac_att_sent', { d: iso(fmtDate(last + 'T12:00:00Z', lang)) })))));
  return sec(ctx, t('wac_sec_message'), kids);
}
function whoBlock(ctx) {
  const { doc, t, integ } = ctx; const c = consentSummary(integ);
  if (!c) return null;                                                  // the status-read package is not applied: no block, never zeros
  const tile = (n, k) => h(doc, 'div', { class: 'wac-tile' }, [h(doc, 'b', { text: String(n) }), h(doc, 'span', { text: t(k) })]);
  return sec(ctx, t('wac_sec_who'), [h(doc, 'div', { class: 'wac-counts' }, [tile(c.canMessage, 'wac_who_ok'), tile(c.noPhone, 'wac_who_nophone'), tile(c.notAgreed, 'wac_who_noconsent')])]);
}
// ---- C5: the editable parts. Everything is disabled while the clinic is paused (the switch stays live); the busy flags disable a control while its request is in flight ----
function toggle(ctx, on, label, focusId, onclick, disabled) {
  const b = h(ctx.doc, 'button', { type: 'button', role: 'switch', class: 'wac-tog' + (on ? ' on' : ''), 'aria-checked': on ? 'true' : 'false', 'aria-label': label, 'data-wac-focus': focusId, disabled: disabled ? true : null });
  b.onclick = onclick; return b;
}
function whenBlock(ctx) {
  const { doc, t, resolved, timing, on, busy, integ } = ctx; const editable = !resolved.paused; const lock = !editable || busy.timing;
  const opts = TIMINGS.map(o => {
    const manualBlock = timing.mode === 'manual' && o === 'before24h'; const dis = lock || manualBlock;
    const input = h(doc, 'input', { type: 'radio', name: 'wac-timing', value: o, id: 'wac-t-' + o, 'data-wac-focus': 't-' + o, disabled: dis ? true : null }); input.checked = timing.timing === o; input.onchange = () => on.chooseTiming(o);
    return h(doc, 'label', { class: 'wac-opt' + (timing.timing === o ? ' on' : '') + (dis ? ' dis' : ''), for: 'wac-t-' + o }, [input, h(doc, 'span', { style: 'min-width:0;flex:1' }, [h(doc, 'span', { class: 'wac-ot', text: t(OPTION_KEYS[o].t) }), h(doc, 'span', { class: 'wac-od', text: t(OPTION_KEYS[o].d) + (manualBlock ? ' ' + t('wac_not_with_manual') : '') })])]);
  });
  const hv = hourVisible(timing);
  const sel = h(doc, 'select', { id: 'wac-hour', 'aria-label': t('wac_aria_hour'), 'data-wac-focus': 'hour', disabled: (lock || !hv) ? true : null }, hourOptions().map(x => { const o = h(doc, 'option', { value: String(x), text: hourLabel(x) }); if (hv && timing.hour === x) o.selected = true; return o; }));
  sel.onchange = () => on.chooseHour(sel.value);
  const mode = (m, label) => { const b = h(doc, 'button', { type: 'button', class: 'wac-seg' + (timing.mode === m ? ' on' : ''), 'aria-pressed': timing.mode === m ? 'true' : 'false', 'data-wac-focus': 'm-' + m, text: label, disabled: (lock || (m === 'manual' && !manualAllowed(timing))) ? true : null }); b.onclick = () => on.chooseMode(m); return b; };
  const example = t(EXAMPLE_KEY[timing.timing], { h: hv ? hourLabel(timing.hour) : '' });
  const dirty = ctx.timingDirty;
  const save = h(doc, 'button', { type: 'button', class: 'ck-btn primary', text: t('wac_save_timing'), 'data-wac': 'save-timing', 'data-wac-focus': 'save', disabled: (!editable || busy.timing || !dirty) ? true : null }); save.onclick = on.saveTiming;
  const lockedRow = (tk, dk) => h(doc, 'label', { class: 'wac-opt dis' }, [h(doc, 'input', { type: 'checkbox', disabled: true, 'aria-disabled': 'true' }), h(doc, 'span', { style: 'min-width:0;flex:1' }, [h(doc, 'span', { class: 'wac-ot', text: t(tk) }), h(doc, 'span', { class: 'wac-od', text: t(dk) })])]);
  return sec(ctx, t('wac_sec_when'), [].concat(opts, [
    h(doc, 'div', { class: 'wac-row' }, [h(doc, 'label', { for: 'wac-hour', class: 'wac-k', text: t('wac_at') }), sel, hv ? null : h(doc, 'span', { class: 'wac-hint', text: t('wac_hour_unused') })]),
    !modeRowVisible(integ) ? null : h(doc, 'div', { class: 'wac-row' }, [h(doc, 'span', { class: 'wac-k', text: t('wac_mode') }), h(doc, 'div', { class: 'wac-segs', role: 'group', 'aria-label': t('wac_mode') }, [mode('automatic', t('wac_mode_auto')), mode('manual', t('wac_mode_manual'))])]),
    h(doc, 'div', { class: 'wac-info', text: example }),
    h(doc, 'div', { class: 'wac-actions' }, [save]),
    h(doc, 'div', { class: 'wac-sh wac-sh2', text: t('wac_locked_head') }), lockedRow('wac_lock1_t', 'wac_lock1_d'), lockedRow('wac_lock2_t', 'wac_lock2_d')
  ]));
}
function shabbatRow(ctx) {
  const { doc, t, integ, resolved, on, busy } = ctx; const state = integ.shabbat_skip !== false;
  return [h(doc, 'div', { class: 'wac-kv' }, kv(ctx, t('wac_shab_k'), h(doc, 'span', { class: 'wac-tr' }, [toggle(ctx, state, t('wac_aria_shab'), 'shabbat', on.toggleShabbat, resolved.paused || busy.shabbat), h(doc, 'span', { text: t('wac_shab_v') })]))), h(doc, 'p', { class: 'wac-hint', text: t('wac_shab_hint') })];
}
function switchBlock(ctx) {
  const { doc, t, resolved, on, busy } = ctx; const paused = resolved.paused === true;
  return sec(ctx, t('wac_sec_switch'), [
    h(doc, 'div', { class: 'wac-kv' }, kv(ctx, t('wac_title'), h(doc, 'span', { class: 'wac-tr' }, [toggle(ctx, !paused, t('wac_aria_switch'), 'switch', on.toggleSwitch, busy.switch), h(doc, 'b', { text: t(paused ? 'wac_chip_paused' : 'wac_sw_on') })]))),
    h(doc, 'p', { class: 'wac-hint', text: t('wac_sw_hint') }),
    h(doc, 'p', { class: 'wac-hint', text: t('wac_pause_note') }),
    h(doc, 'div', { class: 'wac-actions' }, [btn(ctx, t('wac_disc'), 'danger', on.disconnect, { 'data-wac': 'disconnect', 'data-wac-focus': 'disconnect', disabled: busy.disc ? true : null })])
  ]);
}

function connected(ctx) {
  const { doc, t } = ctx;
  const blocks = [connectionBlock(ctx), whenBlock(ctx), quietBlock(ctx), messageBlock(ctx), whoBlock(ctx), switchBlock(ctx)].filter(Boolean);
  return h(doc, 'section', { class: 'wac-card', 'aria-labelledby': 'wac-h' }, [h(doc, 'h2', { id: 'wac-h', text: t('wac_title') }), h(doc, 'p', { class: 'wac-sub', text: t('wac_sub') })].concat(blocks));
}

function declined(ctx) {
  const { doc, t } = ctx;
  return h(doc, 'section', { class: 'wac-card', 'aria-labelledby': 'wac-h' }, [
    h(doc, 'h2', { id: 'wac-h', text: t('wac_title') }),
    h(doc, 'p', { class: 'wac-sub', text: t('wac_declined') })
  ]);
}

function placeholder(ctx, titleKey, subKey, chip) {
  const { doc, t } = ctx;
  return h(doc, 'section', { class: 'wac-card', 'aria-labelledby': 'wac-h' }, [
    h(doc, 'h2', { id: 'wac-h', text: t(titleKey) }),
    subKey ? h(doc, 'p', { class: 'wac-sub', text: t(subKey) }) : null,
    chip ? h(doc, 'div', { class: 'wac-actions' }, [h(doc, 'span', { class: 'wac-chip ' + chip.cls, text: t(chip.key) })]) : null
  ]);
}

function loadError(ctx) {
  const { doc, t } = ctx;
  return h(doc, 'section', { class: 'wac-card', role: 'alert' }, [
    h(doc, 'p', { class: 'wac-sub', text: t('wac_load_error') }),
    h(doc, 'div', { class: 'wac-actions' }, [btn(ctx, t('wac_retry'), 'ghost', ctx.on.retry, { 'data-wac': 'retry' })])
  ]);
}

// returns the card's root element, or null when nothing at all should show (the packages behind the card are not applied yet, or the viewer may not see it)
export function renderCard(ctx) {
  const { doc } = ctx;
  const root = h(doc, 'div', { class: 'wac', 'data-wac-state': '' });
  if (!ctx.load) { root.setAttribute('data-wac-state', 'loading'); root.appendChild(h(doc, 'div', { class: 'wac-skel', 'aria-busy': 'true' })); return root; }
  if (ctx.load.unavailable) return null;
  if (ctx.load.error) { root.setAttribute('data-wac-state', 'error'); root.appendChild(loadError(ctx)); return root; }
  const r = ctx.resolved; root.setAttribute('data-wac-state', r.state);
  let body = null;
  switch (r.state) {
    case STATES.UNAVAILABLE: return null;
    case STATES.NOT_CONNECTED: body = !r.canActivate ? null : (ctx.ui.screen === 'guide' ? guide(ctx) : empty(ctx)); break;
    case STATES.REQUESTED: body = requested(ctx); break;
    case STATES.DECLINED: body = declined(ctx); break;
    case STATES.IN_SETUP: body = inSetup(ctx); break;
    case STATES.CONNECTED: body = ctx.integ ? connected(ctx) : placeholder(ctx, 'wac_title', 'wac_sub', null); break;      // no integration read (cannot happen for a connected state, kept so a half answer never throws)
    default: return null;
  }
  if (!body) return null;
  root.appendChild(body);
  return root;
}

// ---- the dialogs (the cockpit's own ck-dlg-* markup and classes, so they look and close like the other cockpit dialogs) ----
// Returns { close, back, fields }. The backdrop is on document.body, so it survives the Manage view being rebuilt; its draft lives in the caller's cache, not here.
export function openDialog(doc, { title, build, actions, onClose, labelId }) {
  const back = h(doc, 'div', { class: 'ck-dlg-back' });
  const dlg = h(doc, 'div', { class: 'ck-dlg', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': labelId || 'wac-dlg-h' });
  let closed = false;
  const onKey = (e) => { if (e.key === 'Escape') { const all = doc.querySelectorAll('.ck-dlg-back'); if (all.length && all[all.length - 1] !== back) return; e.stopImmediatePropagation(); close(); } };
  const close = () => { doc.removeEventListener('keydown', onKey, true); back.remove(); if (!closed) { closed = true; if (onClose) onClose(); } };
  dlg.appendChild(h(doc, 'div', { class: 'ck-dlg-head', id: labelId || 'wac-dlg-h', text: title }));
  const bd = h(doc, 'div', { class: 'ck-dlg-body' }); const fields = build(bd, close);
  dlg.appendChild(bd);
  const foot = h(doc, 'div', { class: 'ck-dlg-foot' }); const btns = [];
  (actions || []).forEach(a => { const b = h(doc, 'button', { type: 'button', class: 'ck-btn ' + (a.cls || ''), text: a.label, 'data-wac': a.id || null }); b.onclick = () => a.onClick(close, b, btns); foot.appendChild(b); btns.push(b); });
  dlg.appendChild(foot); back.appendChild(dlg); doc.body.appendChild(back);
  back.onclick = (e) => { if (e.target === back) close(); };
  back.__ckClose = close;
  doc.addEventListener('keydown', onKey, true);
  return { close, back, fields, btns };
}
